import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  ArticleRemapServiceError,
  downloadArticleRemapWorkbook,
  fetchArticleRemapData,
} from '../services/articleRemapService';
import {
  ArticleRemapAttributeOption,
  ArticleRemapDataResponse,
  ArticleRemapExportRow,
  ArticleRemapSku,
} from '../types/articleRemap';

interface ArticleRemapDialogProps {
  open: boolean;
  productId: number;
  productNumber: string;
  onClose: () => void;
}

interface EditableRemapRow {
  sku: ArticleRemapSku;
  selected: boolean;
  artNoChangeTo: string;
  attr1NoChangeTo: string;
  attr2CodeChangeTo: string;
  attr3CodeChangeTo: string;
}

interface RemapRowGroup {
  key: string;
  label: string;
  accent: string;
  rows: EditableRemapRow[];
  selectedCount: number;
}

interface RemapColorOption extends ArticleRemapAttributeOption {
  isProductColor: boolean;
}

const GROUP_ACCENTS = [
  '#2563EB',
  '#16A34A',
  '#D97706',
  '#7C3AED',
  '#0891B2',
  '#DB2777',
  '#475569',
];

const emptyToString = (value: string | null | undefined) => value ?? '';

const createEditableRow = (sku: ArticleRemapSku): EditableRemapRow => ({
  sku,
  selected: false,
  artNoChangeTo: sku.articleNo,
  attr1NoChangeTo: emptyToString(sku.attribute1Number),
  attr2CodeChangeTo: emptyToString(sku.attribute2Code),
  attr3CodeChangeTo: emptyToString(sku.attribute3Code),
});

const buildFallbackFilename = (productNumber: string) =>
  `article-remap_${productNumber}_${new Date().toISOString().slice(0, 10)}.xlsx`;

const optionLabel = (option: ArticleRemapAttributeOption) =>
  option.code ? `${option.code} (${option.number})` : option.number;

const compareText = (left: string, right: string) =>
  left.localeCompare(right, 'sv-SE', { numeric: true, sensitivity: 'base' });

const hashString = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const getRowGroupKey = (row: EditableRemapRow) =>
  row.sku.attribute1Number || row.sku.attribute1Code || '__no_color__';

const getColorLabel = (row: EditableRemapRow) => {
  const code = row.sku.attribute1Code || 'Utan färg';
  const number = row.sku.attribute1Number;
  return number ? `${code} (${number})` : code;
};

const getProductColorOptions = (
  rows: EditableRemapRow[],
): ArticleRemapAttributeOption[] => {
  const colorByNumber = new Map<string, ArticleRemapAttributeOption>();

  rows.forEach((row) => {
    const number = row.sku.attribute1Number;
    if (!number || colorByNumber.has(number)) return;

    colorByNumber.set(number, {
      number,
      code: row.sku.attribute1Code || number,
    });
  });

  return Array.from(colorByNumber.values()).sort((left, right) =>
    compareText(optionLabel(left), optionLabel(right)),
  );
};

const groupRemapRows = (rows: EditableRemapRow[]): RemapRowGroup[] => {
  const groupsByKey = new Map<
    string,
    { key: string; label: string; rows: EditableRemapRow[] }
  >();

  rows.forEach((row) => {
    const key = getRowGroupKey(row);
    const current = groupsByKey.get(key);

    if (current) {
      current.rows.push(row);
      return;
    }

    groupsByKey.set(key, {
      key,
      label: getColorLabel(row),
      rows: [row],
    });
  });

  return Array.from(groupsByKey.values())
    .map((group) => ({
      ...group,
      accent: GROUP_ACCENTS[hashString(group.key) % GROUP_ACCENTS.length],
      rows: [...group.rows].sort((left, right) => {
        const sizeCompare = compareText(
          left.sku.attribute2Code || '',
          right.sku.attribute2Code || '',
        );
        if (sizeCompare !== 0) return sizeCompare;

        const lengthCompare = compareText(
          left.sku.attribute3Code || '',
          right.sku.attribute3Code || '',
        );
        if (lengthCompare !== 0) return lengthCompare;

        return compareText(left.sku.pluNo, right.sku.pluNo);
      }),
      selectedCount: group.rows.filter((row) => row.selected).length,
    }))
    .sort((left, right) => compareText(left.label, right.label));
};

const getErrorMessages = (error: unknown): string[] => {
  if (error instanceof ArticleRemapServiceError) {
    return error.messages;
  }

  if (!axios.isAxiosError(error)) {
    return ['Ett oväntat fel inträffade när remap-filen skulle skapas.'];
  }

  const responseData = error.response?.data as
    | {
        error?: string;
        errors?: Array<{ message?: string } | string>;
      }
    | undefined;

  if (!responseData) {
    return [error.message || 'Kunde inte skapa remap-filen.'];
  }

  if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
    return responseData.errors
      .map((item) => (typeof item === 'string' ? item : item.message))
      .filter((message): message is string => Boolean(message));
  }

  return [responseData.error || 'Kunde inte skapa remap-filen.'];
};

const ArticleRemapDialog: React.FC<ArticleRemapDialogProps> = ({
  open,
  productId,
  productNumber,
  onClose,
}) => {
  const [data, setData] = useState<ArticleRemapDataResponse | null>(null);
  const [rows, setRows] = useState<EditableRemapRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;

    setIsLoading(true);
    setErrors([]);
    fetchArticleRemapData(productId)
      .then((response) => {
        if (!isMounted) return;
        setData(response);
        setRows(response.skus.map(createEditableRow));
      })
      .catch((error) => {
        if (!isMounted) return;
        setErrors(getErrorMessages(error));
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [open, productId]);

  const selectedCount = rows.filter((row) => row.selected).length;
  const allSelected = rows.length > 0 && selectedCount === rows.length;
  const partiallySelected = selectedCount > 0 && selectedCount < rows.length;
  const rowGroups = useMemo(() => groupRemapRows(rows), [rows]);

  const attr1Options = data?.attributes.attribute1 ?? [];
  const attr2Options = data?.attributes.attribute2 ?? [];
  const attr3Options = data?.attributes.attribute3 ?? [];

  const productColorOptions = useMemo(
    () => getProductColorOptions(rows),
    [rows],
  );
  const productColorNumbers = useMemo(
    () => new Set(productColorOptions.map((option) => option.number)),
    [productColorOptions],
  );
  const orderedAttr1Options = useMemo<RemapColorOption[]>(() => {
    const optionByNumber = new Map<string, ArticleRemapAttributeOption>();

    attr1Options.forEach((option) => {
      optionByNumber.set(option.number, option);
    });

    productColorOptions.forEach((option) => {
      if (!optionByNumber.has(option.number)) {
        optionByNumber.set(option.number, option);
      }
    });

    return Array.from(optionByNumber.values())
      .map((option) => ({
        ...option,
        isProductColor: productColorNumbers.has(option.number),
      }))
      .sort((left, right) => {
        if (left.isProductColor !== right.isProductColor) {
          return left.isProductColor ? -1 : 1;
        }

        return compareText(optionLabel(left), optionLabel(right));
      });
  }, [attr1Options, productColorNumbers, productColorOptions]);

  const attr1ByNumber = useMemo(
    () => new Map(orderedAttr1Options.map((option) => [option.number, option])),
    [orderedAttr1Options],
  );
  const attr2ByCode = useMemo(
    () => new Map(attr2Options.map((option) => [option.code, option])),
    [attr2Options],
  );
  const attr3ByCode = useMemo(
    () => new Map(attr3Options.map((option) => [option.code, option])),
    [attr3Options],
  );

  const updateRow = (
    pluNo: string,
    patch: Partial<Omit<EditableRemapRow, 'sku'>>,
  ) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        row.sku.pluNo === pluNo ? { ...row, ...patch } : row,
      ),
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setRows((currentRows) =>
      currentRows.map((row) => ({ ...row, selected: checked })),
    );
  };

  const handleSelectGroup = (groupKey: string, checked: boolean) => {
    setRows((currentRows) =>
      currentRows.map((row) =>
        getRowGroupKey(row) === groupKey ? { ...row, selected: checked } : row,
      ),
    );
  };

  const handleExport = async () => {
    const exportRows: ArticleRemapExportRow[] = rows
      .filter((row) => row.selected)
      .map((row) => ({
        pluNoChangeFrom: row.sku.pluNo,
        artNoChangeTo: row.artNoChangeTo.trim(),
        attr1NoChangeTo: row.attr1NoChangeTo.trim(),
        attr2CodeChangeTo: row.attr2CodeChangeTo.trim(),
        attr3CodeChangeTo: row.attr3CodeChangeTo.trim(),
      }));

    if (exportRows.length === 0) {
      setErrors(['Välj minst en PLU-rad att exportera.']);
      return;
    }

    setIsExporting(true);
    setErrors([]);

    try {
      const { blob, filename } = await downloadArticleRemapWorkbook(
        productId,
        exportRows,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || buildFallbackFilename(productNumber);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose();
    } catch (error) {
      setErrors(getErrorMessages(error));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Skapa remap-fil
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {data?.product.productNumber ?? productNumber}
            {data?.product.productName ? ` · ${data.product.productName}` : ''}
          </Typography>
        </Box>
        <IconButton
          aria-label="Stäng"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ m: 2, borderRadius: 1 }}>
            {errors.map((message) => (
              <Typography key={message} variant="body2">
                {message}
              </Typography>
            ))}
          </Alert>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="info" sx={{ borderRadius: 1 }}>
              Det finns inga PLU-rader att remappa för produkten.
            </Alert>
          </Box>
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ px: 2, py: 1.5 }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
              <Chip
                size="small"
                label={`${selectedCount} valda`}
                color={selectedCount > 0 ? 'primary' : 'default'}
                sx={{ borderRadius: 1, fontWeight: 650 }}
              />
              <Typography variant="body2" color="text.secondary">
                {rows.length} PLU-rader
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Grupperade i {rowGroups.length} färger
              </Typography>
            </Stack>

            <TableContainer sx={{ maxHeight: '65vh' }}>
              <Table stickyHeader size="small" aria-label="Remap-rader">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allSelected}
                        indeterminate={partiallySelected}
                        onChange={(event) =>
                          handleSelectAll(event.target.checked)
                        }
                        inputProps={{ 'aria-label': 'Välj alla PLU-rader' }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>PLU från</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Nuvarande</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Artikel till</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Färg till</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>Storlek till</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>Längd till</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowGroups.map((group) => {
                    const groupSelected =
                      group.selectedCount === group.rows.length;
                    const groupPartiallySelected =
                      group.selectedCount > 0 &&
                      group.selectedCount < group.rows.length;

                    return (
                      <React.Fragment key={group.key}>
                        <TableRow
                          sx={{
                            bgcolor: alpha(group.accent, 0.12),
                            '& > .MuiTableCell-root': {
                              borderTop: '1px solid',
                              borderBottom: '1px solid',
                              borderColor: alpha(group.accent, 0.28),
                            },
                          }}
                        >
                          <TableCell
                            padding="checkbox"
                            sx={{
                              borderLeft: '4px solid',
                              borderLeftColor: group.accent,
                            }}
                          >
                            <Checkbox
                              checked={groupSelected}
                              indeterminate={groupPartiallySelected}
                              onChange={(event) =>
                                handleSelectGroup(
                                  group.key,
                                  event.target.checked,
                                )
                              }
                              inputProps={{
                                'aria-label': `Välj alla PLU-rader för ${group.label}`,
                              }}
                            />
                          </TableCell>
                          <TableCell colSpan={6}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={1}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                            >
                              <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 750, color: 'text.primary' }}
                              >
                                {group.label}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${group.rows.length} PLU`}
                                sx={{
                                  borderRadius: 1,
                                  bgcolor: alpha(group.accent, 0.14),
                                  color: 'text.primary',
                                  fontWeight: 650,
                                }}
                              />
                              {group.selectedCount > 0 && (
                                <Chip
                                  size="small"
                                  label={`${group.selectedCount} valda`}
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor: alpha(group.accent, 0.2),
                                    color: 'text.primary',
                                    fontWeight: 650,
                                  }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {group.rows.map((row) => (
                          <TableRow
                            key={row.sku.pluNo}
                            hover
                            selected={row.selected}
                            sx={{
                              bgcolor: row.selected
                                ? alpha(group.accent, 0.11)
                                : alpha(group.accent, 0.035),
                              '&.Mui-selected': {
                                bgcolor: alpha(group.accent, 0.14),
                              },
                              '&.Mui-selected:hover, &:hover': {
                                bgcolor: alpha(group.accent, 0.18),
                              },
                              '& .MuiTextField-root': {
                                bgcolor: 'background.paper',
                              },
                            }}
                          >
                            <TableCell
                              padding="checkbox"
                              sx={{
                                borderLeft: '4px solid',
                                borderLeftColor: group.accent,
                              }}
                            >
                              <Checkbox
                                checked={row.selected}
                                onChange={(event) =>
                                  updateRow(row.sku.pluNo, {
                                    selected: event.target.checked,
                                  })
                                }
                                inputProps={{
                                  'aria-label': `Välj PLU ${row.sku.pluNo}`,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 700 }}
                              >
                                {row.sku.pluNo}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                sx={{ mt: 0.5 }}
                              >
                                {!row.sku.active && (
                                  <Chip
                                    size="small"
                                    color="default"
                                    label="Inaktiv"
                                    sx={{ borderRadius: 1 }}
                                  />
                                )}
                                {!row.sku.published && (
                                  <Chip
                                    size="small"
                                    color="default"
                                    label="Ej publicerad"
                                    sx={{ borderRadius: 1 }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {row.sku.articleNo}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {row.sku.attribute1Code ||
                                row.sku.attribute1Number
                                  ? `${row.sku.attribute1Code ?? '-'} (${row.sku.attribute1Number ?? '-'})`
                                  : '-'}{' '}
                                / {row.sku.attribute2Code || '-'} /{' '}
                                {row.sku.attribute3Code || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={row.artNoChangeTo}
                                onChange={(event) =>
                                  updateRow(row.sku.pluNo, {
                                    artNoChangeTo: event.target.value,
                                  })
                                }
                                size="small"
                                fullWidth
                                inputProps={{ 'aria-label': 'Artikel till' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={orderedAttr1Options}
                                value={
                                  attr1ByNumber.get(row.attr1NoChangeTo) ?? null
                                }
                                getOptionLabel={optionLabel}
                                groupBy={(option) =>
                                  option.isProductColor
                                    ? 'På produkten'
                                    : 'Övriga färger'
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option.number === value.number
                                }
                                onChange={(_, option) =>
                                  updateRow(row.sku.pluNo, {
                                    attr1NoChangeTo: option?.number ?? '',
                                  })
                                }
                                renderGroup={(params) => (
                                  <Box component="li" key={params.key}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        px: 2,
                                        py: 0.75,
                                        bgcolor:
                                          params.group === 'På produkten'
                                            ? alpha('#2563EB', 0.08)
                                            : 'background.default',
                                        color:
                                          params.group === 'På produkten'
                                            ? 'primary.main'
                                            : 'text.secondary',
                                        fontWeight: 750,
                                      }}
                                    >
                                      {params.group}
                                    </Typography>
                                    <Box component="ul" sx={{ p: 0, m: 0 }}>
                                      {params.children}
                                    </Box>
                                  </Box>
                                )}
                                renderOption={(props, option) => (
                                  <Box
                                    component="li"
                                    {...props}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 1.5,
                                    }}
                                  >
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 650 }}
                                        noWrap
                                      >
                                        {optionLabel(option)}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                      >
                                        Attr1-no {option.number}
                                      </Typography>
                                    </Box>
                                    {option.isProductColor && (
                                      <Chip
                                        size="small"
                                        label="På produkten"
                                        color="primary"
                                        variant="outlined"
                                        sx={{
                                          borderRadius: 1,
                                          fontWeight: 650,
                                          flexShrink: 0,
                                        }}
                                      />
                                    )}
                                  </Box>
                                )}
                                renderInput={(params) => (
                                  <TextField {...params} label="Färg" />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={attr2Options}
                                value={
                                  attr2ByCode.get(row.attr2CodeChangeTo) ?? null
                                }
                                getOptionLabel={(option) => option.code}
                                isOptionEqualToValue={(option, value) =>
                                  option.code === value.code
                                }
                                onChange={(_, option) =>
                                  updateRow(row.sku.pluNo, {
                                    attr2CodeChangeTo: option?.code ?? '',
                                  })
                                }
                                renderInput={(params) => (
                                  <TextField {...params} label="Storlek" />
                                )}
                              />
                            </TableCell>
                            <TableCell>
                              <Autocomplete
                                size="small"
                                options={attr3Options}
                                value={
                                  attr3ByCode.get(row.attr3CodeChangeTo) ?? null
                                }
                                getOptionLabel={(option) => option.code}
                                isOptionEqualToValue={(option, value) =>
                                  option.code === value.code
                                }
                                onChange={(_, option) =>
                                  updateRow(row.sku.pluNo, {
                                    attr3CodeChangeTo: option?.code ?? '',
                                  })
                                }
                                renderInput={(params) => (
                                  <TextField {...params} label="Längd" />
                                )}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Avbryt
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={
            isExporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <FileDownloadIcon />
            )
          }
          disabled={isLoading || isExporting || selectedCount === 0}
        >
          Ladda ner Excel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArticleRemapDialog;
